import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { PromotionContestActiveSchema } from '../types/ContestTypes';
import { isContestV1Populated } from '../contestShared';
import { isContestV2Populated } from '../contestV2Types';
import { ContestV1Panel } from './ContestV1Panel';
import { ContestV2Panel } from './ContestV2Panel';
import { StaticTriggerDisplay } from './StaticTriggerDisplay';
import type { PromotionContestV1 } from '../types/ContestTypes';
import type { PromotionContestV2 } from '../types/ContestTypes';

const missingStoryUuidMessage = 'No story UUID configured on this promotion';

interface ContestSectionProps {
  promotionIdentifier: string;
  promotionStoryUuid: string | null;
  savedLockedSchema: PromotionContestActiveSchema | null;
  valuesV1: PromotionContestV1;
  errorsV1: Partial<Record<keyof PromotionContestV1, string>> | undefined;
  touchedV1: Partial<Record<keyof PromotionContestV1, boolean>> | undefined;
  valuesV2: PromotionContestV2;
  errorsV2: unknown;
  touchedV2: unknown;
  activeSchema: PromotionContestActiveSchema;
  onActiveSchemaChange: (schema: PromotionContestActiveSchema) => void;
  onChange: (event: React.ChangeEvent) => void;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  onRewardAmountChangeV1: (value: number) => void;
  onRemoveContest: () => void;
  onSectionExpandedChange?: (expanded: boolean) => void;
}

export function ContestSection(props: ContestSectionProps) {
  const {
    promotionIdentifier,
    promotionStoryUuid,
    savedLockedSchema,
    valuesV1,
    errorsV1,
    touchedV1,
    valuesV2,
    errorsV2,
    touchedV2,
    activeSchema,
    onActiveSchemaChange,
    onChange,
    setFieldValue,
    onRewardAmountChangeV1,
    onRemoveContest,
    onSectionExpandedChange,
  } = props;

  const [sectionExpanded, setSectionExpanded] = useState(
    () => Boolean(savedLockedSchema) || isContestV1Populated(valuesV1) || isContestV2Populated(valuesV2),
  );
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  useEffect(() => {
    onSectionExpandedChange?.(sectionExpanded);
  }, [onSectionExpandedChange, sectionExpanded]);

  useEffect(() => {
    if (savedLockedSchema || isContestV1Populated(valuesV1) || isContestV2Populated(valuesV2)) {
      setSectionExpanded(true);
    }
  }, [savedLockedSchema, valuesV1, valuesV2]);

  const handleRemoveConfirm = () => {
    onRemoveContest();
    setSectionExpanded(false);
    setRemoveConfirmOpen(false);
  };

  const v1Locked = savedLockedSchema === 'v2';
  const v2Locked = savedLockedSchema === 'v1';
  const missingStoryUuid = !promotionStoryUuid?.trim();

  return (
    <>
      <Modal
        show={removeConfirmOpen}
        onHide={() => setRemoveConfirmOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Contest?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will delete the contest configuration for this promotion when you save.
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRemoveConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleRemoveConfirm}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {sectionExpanded ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0 text-dark font-weight-bold">Contest</h5>
          <Button
            type="button"
            variant={null as any}
            className="text-white border-0"
            style={{ backgroundColor: '#888888' }}
            onClick={() => setRemoveConfirmOpen(true)}
          >
            Remove Contest
          </Button>
        </div>
      ) : (
        <h5 className="mb-3 text-dark font-weight-bold">Contest</h5>
      )}

      {!sectionExpanded && (
        <Row className="mb-3">
          <Col>
            <Button
              type="button"
              onClick={() => setSectionExpanded(true)}
            >
              Add Contest
            </Button>
          </Col>
        </Row>
      )}

      {sectionExpanded && (
        <>
          <Row className="mb-3">
            <Col md={12}>
              <StaticTriggerDisplay triggerValue={promotionIdentifier} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <ButtonGroup aria-label="Contest schema version">
                <Button
                  type="button"
                  variant={activeSchema === 'v1' ? 'primary' : 'outline-primary'}
                  onClick={() => onActiveSchemaChange('v1')}
                  disabled={v1Locked}
                  title={v1Locked ? 'Remove the V2 contest before configuring V1' : undefined}
                >
                  V1
                </Button>
                <Button
                  type="button"
                  variant={activeSchema === 'v2' ? 'primary' : 'outline-primary'}
                  onClick={() => onActiveSchemaChange('v2')}
                  disabled={v2Locked}
                  title={v2Locked ? 'Remove the V1 contest before configuring V2' : undefined}
                >
                  V2
                </Button>
              </ButtonGroup>
              {v1Locked && (
                <div className="text-muted small mt-2">
                  A V2 contest is configured. Remove it before configuring V1.
                </div>
              )}
              {v2Locked && (
                <div className="text-muted small mt-2">
                  A V1 contest is configured. Remove it before configuring V2.
                </div>
              )}
            </Col>
          </Row>

          {missingStoryUuid && (
            <Alert variant="danger" className="py-2">
              {missingStoryUuidMessage}
            </Alert>
          )}

          {activeSchema === 'v1' && (
            <ContestV1Panel
              values={valuesV1}
              errors={errorsV1}
              touched={touchedV1}
              promotionStoryUuid={promotionStoryUuid}
              onChange={onChange}
              onRewardAmountChange={onRewardAmountChangeV1}
            />
          )}
          {activeSchema === 'v2' && (
            <ContestV2Panel
              values={valuesV2}
              errors={errorsV2}
              touched={touchedV2}
              promotionStoryUuid={promotionStoryUuid}
              onChange={onChange}
              setFieldValue={setFieldValue}
            />
          )}
        </>
      )}
    </>
  );
}

export { missingStoryUuidMessage };
